import { endOfMonth, format } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { useLazyGetOpenAdvancesQuery } from 'store/api/reports';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { exportOpenAdvancesXlsx } from './openAdvancesExport';

interface OpenAdvancesFormValues {
    asOfDate: string;
}

const OpenAdvancesForm: FC = () => {
    const defaultValues = useMemo<OpenAdvancesFormValues>(
        () => ({ asOfDate: format(endOfMonth(new Date()), 'yyyy-MM-dd') }),
        []
    );

    const methods = useForm<OpenAdvancesFormValues>({ defaultValues, mode: 'onChange' });
    const { control, handleSubmit } = methods;

    const [fetchOpenAdvances, { isFetching }] = useLazyGetOpenAdvancesQuery();
    const [isExporting, setIsExporting] = useState(false);

    const onSubmit = useCallback(
        async (formValues: OpenAdvancesFormValues) => {
            setIsExporting(true);
            try {
                const rows = await fetchOpenAdvances({ asOfDate: formValues.asOfDate }).unwrap();
                await exportOpenAdvancesXlsx(rows, formValues.asOfDate);
            } catch {
                errorAlert(t('reports.open-advances.export-error'));
            } finally {
                setIsExporting(false);
            }
        },
        [fetchOpenAdvances]
    );

    const actions = useMemo(
        () => [
            <Button
                key="open-advances-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                disabled={isFetching || isExporting}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="reports.open-advances.export" />
            </Button>,
        ],
        [handleSubmit, isFetching, isExporting, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('reports.open-advances.title')} actions={actions} />
                <SectionLayout>
                    <Section title="reports.open-advances.title">
                        <div className="flex max-w-xl flex-col gap-4">
                            <div className="w-56">
                                <DateInput
                                    control={control}
                                    name="asOfDate"
                                    label="reports.open-advances.as-of-date"
                                    fullWidth
                                    required
                                />
                            </div>
                        </div>
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

export default OpenAdvancesForm;
